import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Media, Optional } from '@honda-canada/design-system-react';
import ErrorState from './shared/ErrorState';
import LoadingState from './shared/LoadingState';
import { ALL_TAB } from './service/constants';
import DropDownSelectors from './DropdownSelectors/index';
import AccessoriesTabs from './AccessoriesTabs';
import AccessoriesSlider from './AccessoriesSlider';
import { isEmpty } from '../../utils/object';
import AccessoriesConfigurationProvider from './service/Provider';
import Context from './service/Context';
import AccessoryModal from './AccessoryModal';
import { SELECT_ACCESSORY, SHOW_MODAL, SUBMIT_ACCESSORY_TYPE } from './service/reducer';

const AccessoriesUI = () => {
  const {
    accessories,
    accessoriesLength,
    dictionary,
    dispatch,
    dropdownKeys,
    dropdownList,
    selectedOptions,
    selectedAccessoryType,
    showDetailsCard,
    hasError,
    isFetching,
  } = useContext(Context);

  const dropdownSelectorsCta = (
    <Button
      mb={['default', 'xl', 'xl']}
      styling="primary"
      data-testid="cy-accessories-submit-button"
      onClick={() => {
        dispatch({
          type: SUBMIT_ACCESSORY_TYPE,
          payload: selectedAccessoryType,
        });
      }}
    >
      {dictionary.buttonLabel}
    </Button>
  );

  const getOptions = {
    accessoryType: () => {
      if (accessories && !isEmpty(accessories)) {
        const ret = [
          ...Object.keys(accessories).map(key => {
            if (accessories?.[key]?.[0]?.accessoryCategory?.name) {
              return {
                text: `${accessories?.[key]?.[0]?.accessoryCategory?.name} (${accessories?.[key]?.length})`,
                value: key,
                order: accessories?.[key]?.[0]?.accessoryCategory?.displayOrder || 0,
              };
            }
            return null;
          }),
        ]
          .sort((a, b) => parseInt(a.order, 10) - parseInt(b.order, 10))
          .filter(i => i);
        ret.unshift({
          text: `All Accessories (${accessoriesLength})`,
          value: ALL_TAB,
        });
        return ret;
      }
      return [];
    },
  };

  const optionsObject = dropdownKeys?.reduce((acc, key) => {
    acc[key] = getOptions[key]();
    return acc;
  }, {});

  const handlerDropdownChange = (val, key) => {
    if (val === selectedOptions[key]) return;

    dispatch({
      type: `SELECT_${key.toUpperCase()}`,
      payload: val,
    });
  };

  const onSelectAccessory = accessory => {
    dispatch({ type: SELECT_ACCESSORY, payload: accessory });
    dispatch({ type: SHOW_MODAL, payload: true });
  };

  const noAccessories = accessoriesLength === 0;

  return (
    <Box data-testid="select-accessory-box">
      <Optional when={isFetching}>
        <LoadingState />
      </Optional>
      <Optional when={hasError || noAccessories}>
        <ErrorState hasError={hasError} noAccessories={noAccessories} />
      </Optional>
      <Optional when={!isFetching && !hasError && accessoriesLength !== 0}>
        <DropDownSelectors
          cta={dropdownSelectorsCta}
          bodyText={dictionary.priceDisclaimerLabel}
          dropdowns={dropdownList}
          options={optionsObject}
          selectedOptions={selectedOptions}
          onChange={handlerDropdownChange}
        />
        <Optional when={showDetailsCard}>
          <AccessoryModal />
        </Optional>
        <Media greaterThan="mobile">
          <AccessoriesTabs onSelectAccessory={onSelectAccessory} />
        </Media>
        <Media at="mobile">
          <AccessoriesSlider onSelectAccessory={onSelectAccessory} />
        </Media>
      </Optional>
    </Box>
  );
};

const Accessories = ({ selectedTrimId }) => (
  // note: I think variant will be handled by SelectProducts, if so, will make variant dynamic once that's updated
  <AccessoriesConfigurationProvider variant="variant2" selectedTrimId={selectedTrimId}>
    <AccessoriesUI />
  </AccessoriesConfigurationProvider>
);

Accessories.propTypes = {
  selectedTrimId: PropTypes.string.isRequired,
};

export default Accessories;
